import {useCallback, useEffect, useRef, useState} from "react"

const TYPING_SPEED = 40
const DELETING_SPEED = 20
const PAUSE_AFTER_TYPING = 600

const shuffleArray = (array) => {
    const arr = [...array]
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[arr[i], arr[j]] = [arr[j], arr[i]]
    }
    return arr
}

export const useTypewriter = (words, options = {}) => {
    const {enabled = true, pauseOnFocus = true, resetOnWordsChange = false} = options

    const [text, setText] = useState("")
    const [wordIndex, setWordIndex] = useState(0)
    const [charIndex, setCharIndex] = useState(0)
    const [isDeleting, setIsDeleting] = useState(false)
    const [isPaused, setIsPaused] = useState(false)

    const timeoutRef = useRef(null)
    const shuffledWordsRef = useRef(shuffleArray(words))
    const isEnabledRef = useRef(enabled)

    useEffect(() => {
        isEnabledRef.current = enabled
        if (!enabled) {
            setIsPaused(true)
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        } else {
            setIsPaused(false)
        }
    }, [enabled])

    useEffect(() => {
        if (resetOnWordsChange) {
            shuffledWordsRef.current = shuffleArray(words)
            setWordIndex(0)
            setCharIndex(0)
            setIsDeleting(false)
            setText("")
        }
    }, [words, resetOnWordsChange])

    const pause = useCallback(() => setIsPaused(true), [])
    const resume = useCallback(() => setIsPaused(false), [])

    useEffect(() => {
        if (!isEnabledRef.current || isPaused || words.length === 0) {
            return
        }

        const currentWord = shuffledWordsRef.current[wordIndex]

        if (!isDeleting && charIndex === currentWord.length) {
            timeoutRef.current = setTimeout(() => setIsDeleting(true), PAUSE_AFTER_TYPING)
            return
        }

        if (isDeleting && charIndex === 0) {
            setIsDeleting(false)
            setWordIndex((prev) => (prev + 1) % shuffledWordsRef.current.length)
            return
        }

        timeoutRef.current = setTimeout(
            () => {
                const newCharIndex = isDeleting ? charIndex - 1 : charIndex + 1
                setCharIndex(newCharIndex)
                setText(currentWord.substring(0, newCharIndex))
            },
            isDeleting ? DELETING_SPEED : TYPING_SPEED,
        )

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [charIndex, isDeleting, wordIndex, isPaused, words.length])

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    return {text, pause, resume, isPaused}
}
